/**
 * Data Restriction Helpers
 *
 * For roles below Admin (Manager, Salesman, Inventory Staff, Accountant, etc.),
 * sensitive contact lists (Vendors, Customers, Agents, Distributors) are
 * locked down via:
 *   - Search-first UX (min 3 chars, max 10 results)
 *   - Field masking (phone / email / GST / address)
 *   - Hidden export & bulk-select controls
 *   - Watermark overlay
 *   - Audit log on detail open
 */
import api from './api';

export const UNRESTRICTED_ROLES = ['super_admin', 'admin'];

export const isUnrestricted = (user) => UNRESTRICTED_ROLES.includes(user?.role);
export const isRestricted = (user) => !!user && !isUnrestricted(user);

// ── Masking helpers ─────────────────────────────────────────────────────────
const dim = (s = '') => (s || '').toString();

export const maskPhone = (raw) => {
  const s = dim(raw).replace(/\s+/g, '');
  if (!s) return '';
  if (s.length <= 4) return '*'.repeat(s.length);
  return `${s.slice(0, 4)}***${s.slice(-3)}`;
};

export const maskEmail = (raw) => {
  const s = dim(raw);
  if (!s.includes('@')) return s ? s.slice(0, 2) + '***' : '';
  const [name, domain] = s.split('@');
  const head = name.length > 3 ? name.slice(0, 3) : name.slice(0, 1);
  return `${head}***@${domain}`;
};

export const maskGST = (raw) => {
  const s = dim(raw).toUpperCase();
  if (s.length < 6) return s ? '*'.repeat(s.length) : '';
  return `${s.slice(0, 4)}***${s.slice(-3)}`;
};

export const maskAddress = (raw) => {
  const s = dim(raw);
  if (s.length <= 15) return s;
  return `${s.slice(0, 15)}…`;
};

// Conditional mask: returns original if user is unrestricted, masked otherwise
export const cond = (user, fn, val) => (isUnrestricted(user) ? val : fn(val));

// ── Restricted-search helper ────────────────────────────────────────────────
export const MIN_SEARCH_CHARS = 3;
export const MAX_SEARCH_RESULTS = 10;

/** Apply search-first restriction to a list. */
export const applyRestrictedSearch = (user, list, searchTerm) => {
  if (isUnrestricted(user)) return list;
  const q = (searchTerm || '').trim().toLowerCase();
  if (q.length < MIN_SEARCH_CHARS) return [];
  const matches = (list || []).filter(item => {
    const hay = [item.name, item.phone, item.email, item.gst_number, item.city, item.state, item.company]
      .filter(Boolean).map(x => String(x).toLowerCase()).join(' ');
    return hay.includes(q);
  });
  return matches.slice(0, MAX_SEARCH_RESULTS);
};

// ── Audit log helper ────────────────────────────────────────────────────────
export const logRestrictedView = (page, details = '') => {
  api.post('/audit/log', {
    action: 'view_sensitive_detail',
    page,
    details,
  }).catch(() => { /* best-effort, silent */ });
};
